/**
 * Compute a "nice" step from a rough step using 1-2-5 multiples.
 * Returns a positive finite step (guarded to a tiny epsilon).
 */
export function niceStep(roughStep: number) {
    const exponent = Math.floor(Math.log10(Math.max(Math.abs(roughStep), 1e-12)))
    const base = Math.pow(10, exponent)
    const fraction = roughStep / base

    let niceFraction: number
    if (fraction <= 1.5) niceFraction = 1
    else if (fraction <= 3.5) niceFraction = 2
    else if (fraction <= 7.5) niceFraction = 5
    else niceFraction = 10

    return Math.max(niceFraction * base, 1e-12)
}

/**
 * Produce a 'nice' domain (min,max) given raw min/max and desired tickCount.
 * Rounds to 1/2/5 * 10^n steps and adds padding. Also extends to zero when close.
 */
export function niceDomain(rawMin: number, rawMax: number, tickCount = 5) {
    // Guard
    if (!isFinite(rawMin) || !isFinite(rawMax)) {
        return [0, 1]
    }

    // If all equal, choose a small range around the value
    if (rawMax === rawMin) {
        const v = Math.abs(rawMax) || 1
        rawMin = rawMin - v * 0.05
        rawMax = rawMax + v * 0.05
    }

    const range = rawMax - rawMin
    // minimal padding: 2% of max OR 5% of range, at least 1 unit
    const padding = Math.max(1, Math.abs(rawMax) * 0.02, range * 0.05)

    let min = rawMin - padding
    let max = rawMax + padding
    const niceRange = max - min

    // Compute an initial step size
    const roughStep = niceRange / Math.max(1, tickCount - 1)
    // Round step to 1, 2, 5 * 10^n
    const niceStepValue = niceStep(roughStep)

    // Round min down and max up to multiples of niceStep
    min = Math.floor(min / niceStepValue) * niceStepValue
    max = Math.ceil(max / niceStepValue) * niceStepValue

    // If domain includes zero-ish values, prefer extending to zero.
    const zeroTolerance = Math.max(1, Math.abs(max) * 0.02, Math.abs(min) * 0.02)
    if (Math.abs(min) <= zeroTolerance) min = 0
    if (Math.abs(max) <= zeroTolerance) max = 0

    // Defensive: ensure min < max
    if (min === max) {
        if (min === 0) {
            max = 1
        } else {
            min = min - Math.abs(min) * 0.05
            max = max + Math.abs(max) * 0.05
        }
    }

    return [min, max]
}

/**
 * Compute a set of 'nice' tick values between rawMin and rawMax using 1-2-5 steps.
 * Returns { min, max, ticks } where min/max are the rounded domain boundaries and
 * ticks is an array of numeric tick positions (length <= tickCount+1).
 */
export function computeNiceTicks(rawMin: number, rawMax: number, tickCount = 5) {
    const [initialMin, initialMax] = niceDomain(rawMin, rawMax, tickCount)
    let min = initialMin
    const max = initialMax

    const zeroTolerance = Math.max(1, Math.abs(rawMax) * 0.02, Math.abs(rawMin) * 0.02)
    if (rawMin >= 0 && rawMin <= zeroTolerance) {
        min = 0
    }

    const range = Math.max(0, max - min)
    const roughStep = range / Math.max(1, tickCount - 1)
    const step = niceStep(roughStep)

    const ticks: number[] = []
    for (let v = min; v <= max + step / 2; v += step) {
        const rounded = Math.round(v * 100) / 100
        ticks.push(rounded)
    }

    if (min === 0 && ticks.length > 0) {
        ticks[0] = 0
    }

    return { min, max, ticks }
}

export default computeNiceTicks
